import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import AccordionCard from "@whoosh/design-system/src/components/controls/AccordionCard";
import {
  MultiSessionEventIcon,
  SessionIcon,
} from "@whoosh/design-system/src/components/icons_v2/events";
import { PersonFilledIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { EventDetailInfo } from "./EventDetailInfo";
import { EventDetailMultiSessionEventDisplay_event$key } from "./__generated__/EventDetailMultiSessionEventDisplay_event.graphql";

type EventDetailMultiSessionEventDisplayProps = {
  fragmentRef: EventDetailMultiSessionEventDisplay_event$key;
};

export const EventDetailMultiSessionEventDisplay = ({
  fragmentRef,
}: EventDetailMultiSessionEventDisplayProps) => {
  const data = useFragment(
    EventDetailMultiSessionEventDisplayFragmentString,
    fragmentRef
  );

  const numAttendees = data.eventSignUps?.edges?.length ?? 0;

  const isGroupEvent =
    data?.maxGuestCount !== 0 ||
    data?.maxMemberCount !== 1 ||
    data?.minMemberCount !== 1 ||
    data?.minGuestCount !== 0;

  const [firstSession, ...restSessions] = data.eventSessions?.edges ?? [];

  const [showMoreSessions, setShowMoreSessions] = useState(false);

  return (
    <VStack gap={2}>
      <HStack gap={2} alignItems="center">
        <MultiSessionEventIcon color="icon-primary" />
        <Text variant="mainSmallSemiBold">{t`Multi Session`}</Text>
      </HStack>
      <HStack gap={2} alignItems="center">
        <PersonFilledIcon fill="black" color="icon-primary" />
        <Text variant="mainSmallSemiBold">
          {`${isGroupEvent ? t`Groups` : t`Attendees`}: ${numAttendees}${
            data?.capacity ? `/${data.capacity}` : ""
          }`}
        </Text>
      </HStack>
      <VStack alignItems="flex-start" justifyContent="flex-start" pt={4}>
        <AccordionCard
          isOpen={showMoreSessions}
          bgColor="grayscale-0"
          variant="light"
          hideVerticalPadding
          Title={
            <HStack gap={2} alignItems="center">
              <SessionIcon
                style={{ width: 12, height: 72 }}
                color="grayscale.700"
                width="2px"
                height="72px"
              />
              <EventDetailInfo fragmentRef={firstSession?.node ?? null} />
            </HStack>
          }
          hideIcon
          InnerContent={
            <VStack gap={6} height="100%" flex={1}>
              {restSessions.map((e) => (
                <HStack gap={2} key={e?.node?.id} alignItems="flex-start">
                  <Box>
                    <SessionIcon
                      style={{ width: 12, height: 72 }}
                      color="grayscale.700"
                      width="2px"
                      height="72px"
                    />
                  </Box>
                  <EventDetailInfo fragmentRef={e?.node ?? null} />
                </HStack>
              ))}
            </VStack>
          }
        />

        <Button
          style={{ marginTop: 6 }}
          width="auto"
          accessibilityLabel={
            showMoreSessions ? t`Show Fewer Sessions` : t`Show More Sessions`
          }
          variant="secondary"
          onPress={() => setShowMoreSessions(!showMoreSessions)}
        >
          {showMoreSessions ? t`Show Fewer Sessions` : t`Show More Sessions`}
        </Button>
      </VStack>
    </VStack>
  );
};

export const EventDetailMultiSessionEventDisplayFragmentString = graphql`
  fragment EventDetailMultiSessionEventDisplay_event on Event {
    eventSessions(first: 1000) {
      edges {
        node {
          id
          ...EventDetailInfo_eventSession
        }
      }
    }

    capacity
    allowedGroupSizes
    maxGuestCount
    maxMemberCount
    minMemberCount
    minGuestCount
    eventSignUps: signUps(waitlist: false) {
      edges {
        node {
          id
        }
      }
    }
  }
`;
